/**
 * Commission Certificate Upload — Notary of America
 * ===================================================
 * Tag any product with "Notary Commission Certificate Required" to show this upload field.
 * Shopify natively handles file uploads via multipart form — no external storage.
 * Certificate URL saved to sessionStorage so customer only uploads once per session.
 *
 * Property name: "Commission Certificate Required:" — same as KING, order flow unchanged.
 *
 * Add-to-cart flow:
 *   NEW FILE  → native multipart form.submit() (Shopify CDN stores the file)
 *              → page reloads → capturePendingCdnUrl() fetches /cart.js on every page
 *                load, upgrades session from 'pending' → real CDN URL
 *   SAVED URL → AJAX /cart/add.js with hidden property input (CDN URL already known)
 */

(function() {
  'use strict';

  var SESSION_URL_KEY  = 'noa_cert_url';
  var SESSION_NAME_KEY = 'noa_cert_name';
  var PROP_NAME        = 'Commission Certificate Required:';

  // ── Session helpers ───────────────────────────────────────────────────────────

  function saveSession(url, name) {
    try { sessionStorage.setItem(SESSION_URL_KEY, url || ''); sessionStorage.setItem(SESSION_NAME_KEY, name || ''); } catch(e) {}
  }
  function getSessionUrl()  { try { return sessionStorage.getItem(SESSION_URL_KEY) || ''; } catch(e) { return ''; } }
  function getSessionName() { try { return sessionStorage.getItem(SESSION_NAME_KEY) || ''; } catch(e) { return ''; } }
  function clearSession()   { try { sessionStorage.removeItem(SESSION_URL_KEY); sessionStorage.removeItem(SESSION_NAME_KEY); } catch(e) {} }

  // ── CDN capture (runs on EVERY page load) ─────────────────────────────────────
  //
  // After a native multipart POST (new file upload), the browser redirects to the
  // cart page and sessionStorage still holds 'pending'. We immediately fetch
  // /cart.js to find the real Shopify CDN URL and upgrade the session.
  //
  // This also covers the case where the customer navigates to checkout directly
  // from the cart without visiting a product page again — the cart page itself
  // loads this script via layout/theme.liquid, so capture still runs.
  //
  // Returns a Promise that resolves once the capture attempt is complete (whether
  // or not a CDN URL was found), so wireUI can wait on it before deciding what to
  // inject into the form.

  function capturePendingCdnUrl() {
    var pendingName = getSessionName();
    if (getSessionUrl() !== 'pending' || !pendingName) {
      return Promise.resolve(getSessionUrl());
    }

    var promise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        if (!cart.items) return getSessionUrl();
        var found = '';
        cart.items.forEach(function(item) {
          if (!item.properties) return;
          var url = item.properties[PROP_NAME];
          if (url && typeof url === 'string' && url.indexOf('cdn.shopify.com') !== -1) {
            found = url;
          }
        });
        if (found) {
          saveSession(found, pendingName);
          var vl = document.getElementById('noa-cert-view-link');
          if (vl) { vl.href = found; vl.style.display = 'block'; }
        }
        return found || getSessionUrl();
      })
      .catch(function() { return getSessionUrl(); });

    // Expose on window so cart-drawer.js can await this before allowing checkout
    window._noaCertCapturePromise = promise;
    return promise;
  }

  // ── Styles ────────────────────────────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById('noa-cert-styles')) return;
    var css = [
      '@keyframes noa-cert-in { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }',

      '#noa-cert-upload { margin:16px 0; font-family:inherit; animation:noa-cert-in .2s ease both; }',

      '#noa-cert-field-label { display:block; font-size:14px; font-weight:600; color:#333; margin-bottom:3px; }',
      '#noa-cert-field-sub { display:block; font-size:12px; color:#c0392b; font-style:italic; margin-bottom:10px; }',

      '#noa-cert-dropzone {',
      '  border:2px dashed #c8d4e8; border-radius:8px; padding:22px 16px;',
      '  text-align:center; cursor:pointer; background:#f8fafc; position:relative;',
      '  transition:border-color .15s, background .15s;',
      '}',
      '#noa-cert-dropzone:hover { border-color:#1a3a6b; background:#f0f4fb; }',
      '#noa-cert-dropzone.noa-drag { border-color:#1a3a6b; background:#e6eef8; }',

      '#noa-cert-dz-icon { width:42px;height:42px;border-radius:50%;background:#e6eef8;display:flex;align-items:center;justify-content:center;margin:0 auto 10px; }',
      '#noa-cert-dz-title { font-size:14px;font-weight:600;color:#1a3a6b;margin:0 0 4px; }',
      '#noa-cert-dz-sub { font-size:12px;color:#888;margin:0 0 12px; }',
      '#noa-cert-dz-btn { display:inline-block;background:#1a3a6b;color:#fff;border-radius:50px;padding:7px 18px;font-size:13px;font-weight:600;cursor:pointer;transition:background .15s; }',
      '#noa-cert-dz-btn:hover { background:#152d54; }',
      '#noa-cert-file-input { position:absolute;inset:0;opacity:0;width:100%;height:100%;cursor:pointer; }',

      '#noa-cert-onfile { display:none;align-items:center;gap:12px;padding:14px 16px;background:#eaf5ea;border:1.5px solid #b8ddb8;border-radius:8px; }',
      '#noa-cert-onfile.noa-show { display:flex; }',
      '#noa-cert-onfile-icon { width:34px;height:34px;border-radius:50%;background:#2e6b2e;display:flex;align-items:center;justify-content:center;flex-shrink:0; }',
      '#noa-cert-onfile-body { flex:1;min-width:0; }',
      '#noa-cert-onfile-title { font-size:13px;font-weight:700;color:#1a4a1a;margin:0 0 3px; }',
      '#noa-cert-onfile-name { font-size:12px;color:#2e6b2e;margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }',
      '#noa-cert-view-link { font-size:12px;color:#1a3a6b;text-decoration:underline;display:none;margin-top:3px; }',
      '#noa-cert-actions { display:flex;flex-direction:column;gap:6px;flex-shrink:0;align-items:flex-end; }',
      '#noa-cert-change-btn { background:none;border:1.5px solid #b8ddb8;color:#2e6b2e;border-radius:50px;font-size:12px;font-weight:600;padding:5px 12px;cursor:pointer;white-space:nowrap;transition:background .15s; }',
      '#noa-cert-change-btn:hover { background:#d4edd4; }',

      '#noa-cert-error { display:none;align-items:center;gap:8px;padding:10px 14px;background:#fcebeb;border:1.5px solid #f09595;border-radius:8px;font-size:13px;color:#6b1a1a;margin-top:8px; }',
      '#noa-cert-error.noa-show { display:flex; }',
    ].join('\n');
    var s = document.createElement('style');
    s.id = 'noa-cert-styles';
    s.textContent = css;
    document.head.appendChild(s);
  }

  var checkSVG  = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
  var uploadSVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a3a6b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>';
  var warnSVG   = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c0392b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';

  // ── UI builder ────────────────────────────────────────────────────────────────

  function buildUI(insertBefore, form, resolvedUrl) {
    var wrap = document.createElement('div');
    wrap.id = 'noa-cert-upload';
    wrap.innerHTML = [
      '<label id="noa-cert-field-label">Commission Certificate Required: <span style="color:#c0392b">*</span></label>',
      '<span id="noa-cert-field-sub">(Education certificates are not acceptable.)</span>',

      '<div id="noa-cert-dropzone">',
        '<input type="file" id="noa-cert-file-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*" aria-label="Upload commission certificate">',
        '<div id="noa-cert-dz-icon">' + uploadSVG + '</div>',
        '<p id="noa-cert-dz-title">Upload your commission certificate</p>',
        '<p id="noa-cert-dz-sub">PDF, DOC, DOCX, JPG or PNG — max 10MB</p>',
        '<span id="noa-cert-dz-btn">Browse files</span>',
      '</div>',

      '<div id="noa-cert-onfile">',
        '<div id="noa-cert-onfile-icon">' + checkSVG + '</div>',
        '<div id="noa-cert-onfile-body">',
          '<p id="noa-cert-onfile-title">Certificate on file</p>',
          '<p id="noa-cert-onfile-name"></p>',
          '<a id="noa-cert-view-link" href="#" target="_blank" rel="noopener">View uploaded file</a>',
        '</div>',
        '<div id="noa-cert-actions">',
          '<button type="button" id="noa-cert-change-btn">Change</button>',
        '</div>',
      '</div>',

      '<div id="noa-cert-error">' + warnSVG + '<span id="noa-cert-error-msg"></span></div>',
    ].join('');

    insertBefore.parentNode.insertBefore(wrap, insertBefore);
    wireUI(form, resolvedUrl);
  }

  // ── UI wiring ─────────────────────────────────────────────────────────────────
  //
  // resolvedUrl: the session URL after capturePendingCdnUrl() has run.
  // May be a CDN URL, 'pending', or ''.

  function wireUI(form, resolvedUrl) {
    var dropzone   = document.getElementById('noa-cert-dropzone');
    var fileInput  = document.getElementById('noa-cert-file-input');
    var onfile     = document.getElementById('noa-cert-onfile');
    var onfileName = document.getElementById('noa-cert-onfile-name');
    var viewLink   = document.getElementById('noa-cert-view-link');
    var changeBtn  = document.getElementById('noa-cert-change-btn');
    var errorEl    = document.getElementById('noa-cert-error');
    var errorMsg   = document.getElementById('noa-cert-error-msg');

    function showOnFile(name, url) {
      dropzone.style.display = 'none';
      onfile.classList.add('noa-show');
      onfileName.textContent = name;
      errorEl.classList.remove('noa-show');
      if (url && url !== 'pending' && url.indexOf('cdn.shopify.com') !== -1) {
        viewLink.href = url;
        viewLink.style.display = 'block';
      } else if (url && url.indexOf('blob:') === 0) {
        viewLink.href = url;
        viewLink.style.display = 'block';
      } else {
        viewLink.style.display = 'none';
      }
    }

    function showDropzone() {
      dropzone.style.display = '';
      onfile.classList.remove('noa-show');
      errorEl.classList.remove('noa-show');
      if (fileInput) { fileInput.value = ''; fileInput.name = ''; }
      var h = document.getElementById('noa-cert-url-input');
      if (h) h.remove();
    }

    function showError(msg) {
      errorEl.classList.add('noa-show');
      errorMsg.textContent = msg;
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function injectUrlInput(url) {
      var ex = document.getElementById('noa-cert-url-input');
      if (ex) ex.remove();
      if (fileInput) fileInput.name = '';
      var h = document.createElement('input');
      h.type = 'hidden'; h.id = 'noa-cert-url-input';
      h.name = 'properties[' + PROP_NAME + ']';
      h.value = url;
      form.appendChild(h);
    }

    // ── Restore from session ──────────────────────────────────────────────────
    //
    // resolvedUrl is what capturePendingCdnUrl() returned — it has already
    // attempted to upgrade 'pending' to a real CDN URL if one was available.
    //
    // Cases:
    //   CDN URL  → inject hidden input, show "on file" with view link  ✓
    //   'pending'→ file was uploaded but CDN URL not in cart yet (very
    //              unlikely after capture ran, but possible if cart was
    //              empty). Show "on file" UI WITHOUT injecting hidden input;
    //              customer must re-upload or wait. Show a soft notice.
    //   ''       → no session, show dropzone  ✓

    var savedName = getSessionName();

    if (resolvedUrl && resolvedUrl !== 'pending' && savedName) {
      // Happy path — real CDN URL known
      injectUrlInput(resolvedUrl);
      showOnFile(savedName, resolvedUrl);
    } else if (resolvedUrl === 'pending' && savedName) {
      // File was uploaded in this session but CDN URL couldn't be resolved
      // (e.g. cart was cleared between sessions, or cart.js not yet updated).
      // Do NOT inject the hidden input with 'pending' — force re-upload instead.
      clearSession();
      showDropzone();
    } else {
      showDropzone();
    }

    // ── File selected ─────────────────────────────────────────────────────────

    if (fileInput) {
      fileInput.addEventListener('change', function() {
        var file = this.files && this.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
          showError('File too large. Maximum 10MB. Please compress or scan at a lower resolution.');
          this.value = ''; return;
        }

        var ext = file.name.split('.').pop().toLowerCase();
        if (['pdf','doc','docx','jpg','jpeg','png'].indexOf(ext) === -1) {
          showError('Invalid file type. Use PDF, DOC, DOCX, JPG or PNG.');
          this.value = ''; return;
        }

        var localUrl = URL.createObjectURL(file);
        saveSession('pending', file.name);
        this.name = 'properties[' + PROP_NAME + ']';
        var ex = document.getElementById('noa-cert-url-input');
        if (ex) ex.remove();
        showOnFile(file.name, localUrl);
      });
    }

    // ── Drag and drop ─────────────────────────────────────────────────────────

    dropzone.addEventListener('dragover', function(e) { e.preventDefault(); this.classList.add('noa-drag'); });
    dropzone.addEventListener('dragleave', function() { this.classList.remove('noa-drag'); });
    dropzone.addEventListener('drop', function(e) {
      e.preventDefault(); this.classList.remove('noa-drag');
      var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file && fileInput) {
        try { var dt = new DataTransfer(); dt.items.add(file); fileInput.files = dt.files; fileInput.dispatchEvent(new Event('change', {bubbles:true})); }
        catch(err) { fileInput.click(); }
      }
    });

    // ── Change button ─────────────────────────────────────────────────────────

    changeBtn.addEventListener('click', function() {
      showDropzone();
      clearSession();
    });

    // ── Add to Cart validation ────────────────────────────────────────────────

    var addBtn = document.getElementById('AddToCart');
    if (addBtn) {
      addBtn.addEventListener('click', function(e) {
        var hasFile   = fileInput && fileInput.files && fileInput.files.length > 0;
        var hasUrlEl  = !!document.getElementById('noa-cert-url-input');
        // 'pending' is no longer accepted as valid — if capture failed and
        // session is pending, we already cleared it and showed the dropzone above.
        // So the only valid states here are: real file selected, or CDN URL injected.
        if (!hasFile && !hasUrlEl) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showError('Please upload your commission certificate before adding to cart.');
          return false;
        }
      }, true);
    }

    // ── Post-AJAX add: capture CDN URL ────────────────────────────────────────
    // Fires when cart drawer AJAX path completes (no page reload needed there).

    document.addEventListener('noa-cart-added', function() {
      setTimeout(function() {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            cart.items.forEach(function(item) {
              if (!item.properties) return;
              var url = item.properties[PROP_NAME];
              if (url && typeof url === 'string' && url.indexOf('cdn.shopify.com') !== -1) {
                saveSession(url, getSessionName());
                injectUrlInput(url);
                var vl = document.getElementById('noa-cert-view-link');
                if (vl) { vl.href = url; vl.style.display = 'block'; }
                var onfileEl = document.getElementById('noa-cert-onfile');
                if (onfileEl) onfileEl.classList.add('noa-show');
              }
            });
          })
          .catch(function() {});
      }, 1000);
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────────

  function init() {
    // Step 1: always attempt CDN capture first (no-op if session isn't 'pending').
    // This returns a Promise so wireUI receives the resolved URL synchronously.
    capturePendingCdnUrl().then(function(resolvedUrl) {

      var form = document.getElementById('AddToCartForm');
      if (!form) return;
      if (form.getAttribute('data-requires-cert') !== 'true') return;
      if (document.getElementById('noa-cert-upload')) return;

      injectStyles();

      var insertPoint = document.querySelector('.product-single__prices')
                     || document.querySelector('.product-single__quantity')
                     || document.getElementById('AddToCart');
      if (!insertPoint) return;

      buildUI(insertPoint, form, resolvedUrl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
